import { motion } from 'framer-motion';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: 'easeOut' },
});

const trustBadges = ['PHIPA-Aligned', 'Canadian Servers', 'Zero Model Training', 'Zero Known Breaches'];

export default function Hero() {
  return (
    <section className="relative bg-navy-900 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <div className="max-w-3xl">

          <motion.p
            className="text-lg sm:text-xl font-medium text-white/50 tracking-tight mb-5"
            {...fadeUp(0)}
          >
            Now in pilot with Canadian physicians and clinics.
          </motion.p>
          {/* PLACEHOLDER: Once first named pilot clinic confirms, replace the line above with:
              "Used by [Clinic Name], [City], ON — now available to your practice." */}

          {/* Headline */}
          <motion.h1
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[1.08] tracking-tight"
            {...fadeUp(0.1)}
          >
            Clinical admin,{' '}
            <span className="text-primary-400">handled by AI.</span>
          </motion.h1>

          {/* Sub */}
          <motion.p
            className="mt-6 text-lg sm:text-xl text-white/65 leading-relaxed max-w-2xl"
            {...fadeUp(0.2)}
          >
            Canadian clinics are buried under legacy EMRs, fax piles, and documentation
            that stretches well past 5pm. We build tools to give that time back.
          </motion.p>

          {/* CTAs */}
          <motion.div
            className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4"
            {...fadeUp(0.3)}
          >
            <a
              href="https://calendly.com/pulsestack1/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-lg text-white bg-primary-600 hover:bg-primary-500 transition-colors shadow-lg shadow-primary-900/40 min-h-[52px]"
            >
              Book a Demo
            </a>
            <a
              href="#products"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-lg text-white border border-white/25 hover:bg-white/10 transition-colors min-h-[52px]"
            >
              See Our Products ↓
            </a>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            className="flex flex-wrap gap-2 mt-8"
            {...fadeUp(0.4)}
          >
            {trustBadges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-white/10 text-white/75 border border-white/15 backdrop-blur-sm"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                {badge}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Floating product screenshots */}
        <motion.div
          className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 w-[420px] xl:w-[480px]"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
        >
          <div className="relative">
            <div className="bg-navy-800/80 backdrop-blur rounded-xl border border-white/10 shadow-2xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-green-400/60" />
                <div className="ml-3 flex-1 bg-white/5 rounded px-3 py-1 text-xs text-white/30">
                  aura-chart.pulsestack.ai
                </div>
              </div>
              <img
                src="/aurachart-main.png"
                alt="Aura-Chart interface"
                className="w-full object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl px-4 py-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <svg className="h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-900">SOAP note ready</p>
                <p className="text-xs text-gray-400">Generated in 24 seconds</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
